import { useEffect } from 'react';

import { usePsfSharePageData } from 'src/app/pages/psf/share/pageContext';
import type { ShareFeedItem } from 'src/app/pages/psf/share/pageContext';
import { usePageCopy, useShouldShowSharingGoalFlow } from 'src/app/pages/psf/share/shared/hooks';
import { useTrackOnce } from 'src/app/shared/hooks/tracking';
import { useIsMobile } from 'src/app/shared/hooks/userAgent';

type Result = ModelHookResult<{
	heading: string;
	subHeading: string;
	warning?: string;
	shareFeed: {
		items: readonly ShareFeedItem[];
		showFeed: boolean;
	};
	isPsfOnlyShare: boolean;
	pageTitle: string;
	shouldShowSharingGoalFlow: boolean;
	showPetitionMedia: boolean;
	desktopShareAssetHeader: { url: string } | undefined;
}>;

const MIN_SHARES = 3;

export function usePageBody(): Result {
	const {
		data: { headingText, subheadingText, progressBarLabelText },
	} = usePageCopy();
	const {
		data: {
			petition,
			shareFeed,
			featureConfigs: { warningMessage, psfOnlyShareButtonList, isShareAssetsEnabled },
		},
	} = usePsfSharePageData();
	const trackOnce = useTrackOnce();
	const isMobile = useIsMobile();

	const showFeed = shareFeed.length >= MIN_SHARES;

	const pageTitle = progressBarLabelText;

	const shouldShowSharingGoalFlow = useShouldShowSharingGoalFlow();

	useEffect(() => {
		// For sharing goal flow, we track the feed view after the goal is set.
		if (!showFeed || shouldShowSharingGoalFlow) return;
		void trackOnce('share_feed_view', {
			share_feed_item_count: shareFeed.length,
			share_feed_photo_count: shareFeed.filter((item) => !!item.userPhoto).length,
		});
	}, [trackOnce]);

	const shareVisualUrl = petition.bestShareVisual?.url;
	const isDesktopShareAssetVisible = !isMobile && isShareAssetsEnabled && !!shareVisualUrl;
	const desktopShareAssetHeader = isDesktopShareAssetVisible ? { url: shareVisualUrl } : undefined;

	return {
		data: {
			heading: headingText,
			subHeading: subheadingText,
			warning: warningMessage || undefined,
			shareFeed: {
				items: shareFeed,
				showFeed,
			},
			isPsfOnlyShare: psfOnlyShareButtonList.length > 0,
			pageTitle,
			shouldShowSharingGoalFlow,
			showPetitionMedia: !isDesktopShareAssetVisible,
			desktopShareAssetHeader,
		},
	};
}
